import { useState } from "react";
import SecuritySection from "./SecuritySection";
import axios from "axios";
import { API } from "../../config";
import { AlertCircle, ArrowUp, BarChart3, CheckCircle, Clock, Eye, GitCompare, Globe, Lock, Sparkles, Users, Zap } from "lucide-react";
import { ScoreCircle } from "../common/ScoreCircle";
import { ShareToUnlock } from "../common/ShareToUnlock";

// Inline blur+upgrade overlay used by every paywalled section. Stays inside
// the host card so the layout doesn't shift between free and paid views.
const PaidLock = ({ ctaMessage, priceEur = 49, testId, onUpgrade }) => (
  <div
    className="absolute inset-0 z-10 flex flex-col items-center justify-center backdrop-blur-md bg-[var(--color-bg)]/70 border-2 border-dashed border-[#a855f7]/40 rounded"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-2">
      <Lock className="w-5 h-5 text-[#a855f7]" />
      <span className="text-xs font-black uppercase tracking-wider text-[#a855f7]">PRO</span>
    </div>
    <p className="text-sm font-semibold text-center max-w-xs px-4 mb-3">{ctaMessage}</p>
    <button
      onClick={onUpgrade}
      data-testid={`${testId}-cta`}
      className="px-4 py-2 text-xs font-black bg-[#a855f7] text-white rounded hover:bg-purple-600 transition"
    >
      Odomknúť za €{priceEur}/mes
    </button>
    <p className="mt-2 text-[10px] text-[var(--color-text-muted)] text-center px-4" data-testid={`${testId}-trust`}>
      Zrušiteľné kedykoľvek · Žiadne skryté poplatky · GDPR · Stripe secure
    </p>
  </div>
);

export const AnalysisResults = ({ data, t, onCompare, analysisHistory, isLoadingHistory, language, userEmail }) => {
  const [rescanFreq, setRescanFreq] = useState('weekly');
  const [rescanScheduled, setRescanScheduled] = useState(false);
  const [fullReportUnlocked, setFullReportUnlocked] = useState(false);

  // Paywall flags coming from backend `_apply_free_teaser`. When `is_paid` is
  // true (Starter+ or admin), `locked_sections` is null and we render
  // everything as before.
  const isPaid = data.is_paid === true || data.plan === "paid";
  const lockedSet = new Set(data.locked_sections || []);
  const isLocked = (key) => !isPaid && lockedSet.has(key);
  const competitorRows = isLocked("competitors")
    ? [
        { domain: "konkurent-1.sk", strengths: ["Silný obsah", "FAQ schema"] },
        { domain: "konkurent-2.sk", strengths: ["Lokálne SEO", "Recenzie"] },
        { domain: "konkurent-3.sk", strengths: ["Rýchly web", "Blog"] },
      ]
    : data.competitors || [];
  const upgradeMsg = data.upgrade_cta?.[`message_${language || 'sk'}`]
    || data.upgrade_cta?.message_sk
    || "Odomkni plný report za €49/mes";
  const goUpgrade = () => {
    window.location.href = data.upgrade_cta?.url || "/pricing?upgrade=starter";
  };

  const handleScheduleRescan = async () => {
    try {
      await axios.post(`${API}/rescan/schedule`, {
        url: data.url,
        email: userEmail || '',
        frequency: rescanFreq,
        language: language || 'en'
      });
      setRescanScheduled(true);
      setTimeout(() => setRescanScheduled(false), 3000);
    } catch (e) {
      console.error('Schedule rescan error:', e);
    }
  };

  return (
    <div className="py-16 px-4 bg-[var(--color-bg)]" data-testid="analysis-results">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-black tracking-tight font-['Chivo'] mb-4">{t.results_title}</h2>
          <p className="text-[var(--color-text-muted)]">{data.url}</p>
          
          {/* Compare button */}
          {analysisHistory && analysisHistory.length > 1 && (
            <button
              data-testid="compare-button"
              onClick={() => onCompare(analysisHistory[1].id, data.id)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#a855f7] text-white font-semibold hover:bg-blue-700 transition-colors"
            >
              <GitCompare className="w-4 h-4" />
              {t.compare_with_previous}
            </button>
          )}
          
          {analysisHistory && analysisHistory.length === 1 && (
            <p className="mt-4 text-sm text-[var(--color-text-muted)]">{t.no_previous_analysis}</p>
          )}

          {/* Schedule Rescan */}
          <div className="mt-6 inline-flex items-center gap-2 flex-wrap justify-center" data-testid="rescan-scheduler">
            <select
              data-testid="rescan-frequency"
              value={rescanFreq}
              onChange={(e) => setRescanFreq(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] text-sm bg-[var(--color-surface)] focus:border-blue-600 focus:outline-none"
            >
              <option value="daily">{t.rescan_daily}</option>
              <option value="weekly">{t.rescan_weekly}</option>
              <option value="monthly">{t.rescan_monthly}</option>
            </select>
            <button
              data-testid="schedule-rescan-btn"
              onClick={handleScheduleRescan}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors ${
                rescanScheduled 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {rescanScheduled ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
              {rescanScheduled ? t.rescan_scheduled : t.schedule_rescan}
            </button>
          </div>
        </div>
        
        {/* Main Score */}
        <div className="flex justify-center mb-12 fade-in-up">
          <div className="text-center">
            <ScoreCircle score={data.overall_score} size={160} strokeWidth={12} label="Overall" showGrade />
            <p className="mt-4 text-sm text-[var(--color-text-muted)] uppercase tracking-wider font-semibold">Overall Score</p>
          </div>
        </div>

        {/* Potential Boost Banner */}
        {data.potential_boost && data.potential_boost > 0 && (
          <div className="mb-12 p-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white fade-in-up" data-testid="potential-boost-banner">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <h3 className="text-lg font-bold font-['Chivo'] mb-1">{t.potential_title}</h3>
                <p className="text-blue-100 text-sm">{t.potential_desc} <span className="text-2xl font-black">+{data.potential_boost}%</span></p>
              </div>
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <p className="text-3xl font-mono font-black">{data.overall_score}</p>
                  <p className="text-xs text-blue-200 mt-1">{t.potential_current}</p>
                </div>
                <ArrowUp className="w-8 h-8 text-green-300 animate-bounce" />
                <div className="text-center">
                  <p className="text-3xl font-mono font-black text-green-300">{data.projected_score || Math.min(100, data.overall_score + data.potential_boost)}</p>
                  <p className="text-xs text-blue-200 mt-1">{t.potential_projected}</p>
                </div>
              </div>
              {/* Mini bar chart showing growth */}
              <div className="flex items-end gap-1 h-12">
                {[0.4, 0.5, 0.6, 0.7, 0.8, 0.85, 0.9, 1.0].map((h, i) => (
                  <div key={i} className="w-2 rounded-t bg-[var(--color-surface)]/30 transition-all" style={{ height: `${h * 48}px`, animationDelay: `${i * 100}ms` }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Share to Unlock Full Report */}
        {!fullReportUnlocked && (
          <div className="mb-12 fade-in-up">
            <ShareToUnlock
              score={data.overall_score}
              grade={data.grade || 'C'}
              url={data.url}
              onUnlocked={() => setFullReportUnlocked(true)}
            />
          </div>
        )}
        
        {/* AI Visibility Section */}
        <div className="mb-12 fade-in-up-delay-1 relative" data-testid="ai-visibility-section">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-2 font-['Chivo']">
            <Eye className="w-5 h-5 text-[#a855f7]" />
            {t.ai_visibility}
            {isLocked("ai_visibility_breakdown") && (
              <Lock className="w-4 h-4 text-[#a855f7] ml-1" />
            )}
          </h3>
          <div className="analysis-grid relative">
            <div className="analysis-cell text-center" data-testid="chatgpt-score">
              <ScoreCircle score={data.ai_visibility.chatgpt_ready ?? 50} size={80} strokeWidth={6} />
              <p className="mt-2 text-sm font-medium">{t.chatgpt_ready}</p>
            </div>
            <div className="analysis-cell text-center" data-testid="perplexity-score">
              <ScoreCircle score={data.ai_visibility.perplexity_ready ?? 50} size={80} strokeWidth={6} />
              <p className="mt-2 text-sm font-medium">{t.perplexity_ready}</p>
            </div>
            <div className="analysis-cell text-center" data-testid="gemini-score">
              <ScoreCircle score={data.ai_visibility.gemini_ready ?? 50} size={80} strokeWidth={6} />
              <p className="mt-2 text-sm font-medium">{t.gemini_ready}</p>
            </div>
            <div className="analysis-cell text-center" data-testid="voice-search-score">
              <ScoreCircle score={data.ai_visibility.voice_search_score ?? 50} size={80} strokeWidth={6} />
              <p className="mt-2 text-sm font-medium">{t.voice_search}</p>
            </div>
            {isLocked("ai_visibility_breakdown") && (
              <PaidLock
                ctaMessage={upgradeMsg}
                priceEur={data.upgrade_cta?.price_eur || 49}
                testId="ai-visibility-lock"
                onUpgrade={goUpgrade}
              />
            )}
          </div>
        </div>
        
        {/* SEO & GEO Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12 fade-in-up-delay-2">
          {/* SEO Audit */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="seo-audit">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <BarChart3 className="w-5 h-5 text-[#a855f7]" />
                {t.seo_audit}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">Title</span>
                <span className={`text-sm font-medium ${data.seo_metrics.title_length >= 30 && data.seo_metrics.title_length <= 60 ? 'text-green-600' : 'text-orange-500'}`}>
                  {data.seo_metrics.title_length} chars
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">Meta Description</span>
                <span className={`text-sm font-medium ${data.seo_metrics.meta_description_length >= 120 ? 'text-green-600' : 'text-orange-500'}`}>
                  {data.seo_metrics.meta_description_length} chars
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">H1 Tags</span>
                <span className={`text-sm font-medium ${data.seo_metrics.h1_count === 1 ? 'text-green-600' : 'text-orange-500'}`}>
                  {data.seo_metrics.h1_count}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">Images without ALT</span>
                <span className={`text-sm font-medium ${data.seo_metrics.images_without_alt === 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {data.seo_metrics.images_without_alt}/{data.seo_metrics.total_images}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">HTTPS</span>
                {data.seo_metrics.is_https ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">Word Count</span>
                <span className="text-sm font-medium">{data.seo_metrics.word_count}</span>
              </div>
            </div>
          </div>
          
          {/* GEO Analysis */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="geo-analysis">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <Globe className="w-5 h-5 text-[#a855f7]" />
                {t.geo_analysis}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">{t.detected_region}</span>
                <span className="text-sm font-medium">{data.geo_analysis.detected_region}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">{t.local_keywords}</span>
                <span className="text-sm font-medium">{data.geo_analysis.local_keywords_found}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">NAP Consistency</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#a855f7] progress-bar" 
                      style={{ width: `${data.geo_analysis.nap_consistency}%` }}
                    />
                  </div>
                  <span className="text-sm font-mono">{data.geo_analysis.nap_consistency}%</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">{t.local_schema}</span>
                {data.geo_analysis.local_schema ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-orange-500" />
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Backlinks (own AI agent — no Ahrefs/SEMrush dependency) */}
        {data.backlinks && (
          <div className="mb-12 fade-in-up-delay-2" data-testid="backlinks-section">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2 font-['Chivo']">
              <ArrowUp className="w-5 h-5 text-[#a855f7]" />
              Backlinks · Domain Authority
              <span className="ml-2 px-2 py-0.5 text-[9px] font-black bg-emerald-500/15 text-emerald-500 rounded uppercase">
                AI Agent
              </span>
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Score card */}
              <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-6 flex flex-col items-center" data-testid="backlinks-score-card">
                <ScoreCircle score={data.backlinks.score || 0} size={100} strokeWidth={8} label="Backlink skóre" showGrade />
                <p className="mt-4 text-2xl font-black font-['Chivo']" data-testid="backlinks-count">
                  {data.backlinks.estimated_count ?? 0}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider">≈ backlinkov</p>
              </div>

              {/* DA + indexed pages */}
              <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-6 space-y-4">
                <div>
                  <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Domain Authority</p>
                  <p className="text-2xl font-black font-['Chivo']" data-testid="backlinks-da">
                    {data.backlinks.domain_authority ?? "—"}<span className="text-base text-[var(--color-text-muted)]">/100</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Indexované stránky</p>
                  <p className="text-xl font-bold font-['Chivo']" data-testid="backlinks-indexed">
                    {data.backlinks.indexed_pages == null
                      ? <span className="text-[var(--color-text-muted)]">🔒 PRO</span>
                      : data.backlinks.indexed_pages}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mb-1">Spomínajúce domény</p>
                  <p className="text-xl font-bold font-['Chivo']">{data.backlinks.unique_mentioning_domains ?? 0}</p>
                </div>
              </div>

              {/* Top mentioners list (paywall) */}
              <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-6 relative" data-testid="backlinks-mentioners-card">
                <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mb-3">Top spomínajúce</p>
                {(data.backlinks.top_mentioners || []).length > 0 ? (
                  <ul className="space-y-2 text-sm">
                    {data.backlinks.top_mentioners.slice(0, 8).map((d, i) => (
                      <li key={i} className="flex items-center gap-2 font-mono text-[var(--color-text-muted)]">
                        <span className="text-[#a855f7]">·</span>
                        <span className="truncate" title={d}>{d}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-[var(--color-text-muted)] italic">
                    {isLocked("backlinks_full")
                      ? "Zoznam zdrojov dostupný v Pro pláne."
                      : "Zatiaľ žiadne externé spomenutia."}
                  </p>
                )}
                {isLocked("backlinks_full") && (
                  <PaidLock
                    ctaMessage="Vidieť všetkých zdrojov + AI link-building odporúčania"
                    priceEur={data.upgrade_cta?.price_eur || 49}
                    testId="backlinks-lock"
                    onUpgrade={goUpgrade}
                  />
                )}
              </div>
            </div>
            <p className="text-[10px] text-[var(--color-text-muted)] mt-2 italic">
              ⚡ Vlastný AI agent · žiadny Ahrefs/SEMrush závislosť · {data.backlinks.source}
            </p>
          </div>
        )}

        {/* Performance & Competitors */}
        <div className="grid md:grid-cols-2 gap-8 mb-12 fade-in-up-delay-3">
          {/* Performance */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="performance-metrics">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <Zap className="w-5 h-5 text-[#a855f7]" />
                {t.performance}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">{t.load_time}</span>
                <span className="text-sm font-medium">{data.performance.load_time_estimate}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">{t.mobile_friendly}</span>
                {data.performance.mobile_friendly ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">{t.core_web_vitals}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 progress-bar" 
                      style={{ width: `${data.performance.core_web_vitals_score}%` }}
                    />
                  </div>
                  <span className="text-sm font-mono">{data.performance.core_web_vitals_score}</span>
                </div>
              </div>
            </div>
          </div>
          
          <SecuritySection security={data.security} />

          {/* Competitors */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)] relative" data-testid="competitors">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <Users className="w-5 h-5 text-[#a855f7]" />
                {t.competitors}
                {isLocked("competitors") && <Lock className="w-3.5 h-3.5 text-[#a855f7]" />}
              </h3>
            </div>
            <div className="p-4 space-y-3 min-h-[180px]">
              {competitorRows.length === 0 && (
                <p className="text-sm text-[var(--color-text-muted)]" data-testid="competitors-empty">Konkurentov sa nepodarilo overiť.</p>
              )}
              {competitorRows.map((comp, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-[var(--color-border)] last:border-0">
                  <div>
                    <p className="text-sm font-medium">{comp.domain}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">{(comp.strengths || []).join(" · ")}</p>
                  </div>
                  <a href={`https://${comp.domain}`} target="_blank" rel="noreferrer nofollow" className="text-xs text-[#a855f7] hover:underline" data-testid={`competitor-link-${i}`}>Otvoriť</a>
                </div>
              ))}
            </div>
            {isLocked("competitors") && (
              <PaidLock
                ctaMessage={`Vidieť všetkých ${data.competitors_total ?? 5}+ konkurentov`}
                priceEur={data.upgrade_cta?.price_eur || 49}
                testId="competitors-lock"
                onUpgrade={goUpgrade}
              />
            )}
          </div>
        </div>
        
        {/* AI Solutions */}
        <div className="mb-12" data-testid="ai-solutions">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-2 font-['Chivo']">
            <Sparkles className="w-5 h-5 text-[#a855f7]" />
            {t.ai_solutions}
            {isLocked("ai_solutions_full") && (
              <span className="ml-2 px-2 py-0.5 text-[10px] font-black bg-[#a855f7]/15 text-[#a855f7] rounded uppercase">
                {data.ai_solutions_locked_count}+ ďalších v Pro
              </span>
            )}
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {data.ai_solutions.map((solution, i) => (
              <div key={i} className="solution-card" data-testid={`solution-${i}`}>
                <div className="flex items-start justify-between mb-4">
                  <span className={`impact-badge ${solution.impact === 'High' ? 'impact-high' : solution.impact === 'Medium' ? 'impact-medium' : 'impact-low'}`}>
                    {solution.impact} {t.impact}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">#{solution.priority}</span>
                </div>
                <h4 className="font-bold mb-2 font-['Chivo']">{solution.title}</h4>
                <p className="text-sm text-[var(--color-text-muted)] mb-4">{solution.description}</p>
                <div className="mb-4">
                  <span className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider">{t.effort}: </span>
                  <span className="text-xs font-medium">{solution.effort}</span>
                </div>
                <div>
                  <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mb-2">{t.actions}:</p>
                  <ul className="space-y-1">
                    {solution.actions.map((action, j) => (
                      <li key={j} className="text-sm flex items-start gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500 mt-1 flex-shrink-0" />
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
            {isLocked("ai_solutions_full") && (
              <div
                className="solution-card relative flex flex-col items-center justify-center text-center p-8 border-2 border-dashed border-[#a855f7]/40 bg-[#a855f7]/5 cursor-pointer hover:bg-[#a855f7]/10 transition"
                onClick={goUpgrade}
                data-testid="ai-solutions-lock-card"
              >
                <Lock className="w-8 h-8 text-[#a855f7] mb-3" />
                <h4 className="font-black text-lg mb-2 font-['Chivo']">
                  +{data.ai_solutions_locked_count} ďalších AI fixov
                </h4>
                <p className="text-sm text-[var(--color-text-muted)] mb-4">
                  Detailný akčný plán pre každý problém + odhadovaný ROI €/mes
                </p>
                <button
                  className="px-5 py-2 text-sm font-black bg-[#a855f7] text-white rounded hover:bg-purple-600 transition"
                  data-testid="ai-solutions-lock-cta"
                >
                  Odomknúť za €{data.upgrade_cta?.price_eur || 49}/mes
                </button>
                <p className="mt-2 text-[10px] text-[var(--color-text-muted)] text-center" data-testid="ai-solutions-lock-trust">
                  Zrušiteľné kedykoľvek · Žiadne skryté poplatky · GDPR · Stripe secure
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
